import { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import AgendaItemTimerControls from './AgendaItemTimerControls';
import useDebateSpeaker from './useDebateSpeaker';
import DebateTurnTicks from './DebateTurnTicks';
import { getDebateTurns } from 'utils/debateTurns';
import { getActivityTimingLimits } from 'utils/activityTiming';
import Limits from './modals/eventTypes/SharedComponents/Limits';

export default function DebateAgendaTimers({ item, meeting, getMeeting, isTimerManager, isLocked, showClockText = true, centerControl = null, showSpeaker = true, speakerToggle = null }) {
  const turns = useMemo(() => getDebateTurns(item.activity, item.assignedUser), [item.activity, item.assignedUser]);
  const mainTimerRef = useRef(null);
  const { turn, index, select, running, liveState, updateTimerState } = useDebateSpeaker({
    item, meeting, turns, canControl: isTimerManager && !isLocked,
  });
  const speakerName = turn?.speaker
    ? `${turn.speaker.firstName || ''} ${turn.speaker.lastName || ''}`.trim() : 'Speaker not assigned';
  const debateLimits = getActivityTimingLimits(item.activity) || item.agendaEvent;
  const speakerLimits = turn ? getActivityTimingLimits({ type: 'debate', segments: [turn] }) : null;
  return <div className={`debate-agenda-timers${speakerToggle ? ' has-speaker-toggle' : ''}`} aria-label="Debate agenda timers">
    <div className="debate-agenda-timer-column text-center" role="group" aria-label="Entire debate timer">
      {speakerToggle && <div className="debate-agenda-toggle-row" aria-hidden="true" />}
      <div className="mb-2" role="group" aria-label="Entire debate timing limits">
        <Limits item={{ agendaEvent: debateLimits }} readOnly compact />
      </div>
      <small className="d-block">Entire debate</small>
      <AgendaItemTimerControls item={{ ...item, agendaEvent: debateLimits }}
        ref={mainTimerRef}
        meeting={meeting} getMeeting={getMeeting} isTimerManager={isTimerManager}
        readOnly={!isTimerManager || isLocked} isLocked={isLocked} showClockText={showClockText} />
    </div>
    <div className="debate-agenda-timer-center">{centerControl}</div>
    <div className="debate-agenda-timer-column text-center">
    {speakerToggle && <div className="debate-agenda-toggle-row">{speakerToggle}</div>}
    {showSpeaker && <div role="group" aria-label="Current speaker timer">
      <div className="mb-2" role="group" aria-label="Current speaker timing limits">
        <Limits item={{ agendaEvent: speakerLimits }} readOnly compact />
      </div>
      <small className="d-block">Current speaker</small>
      {turn ? <>
        <AgendaItemTimerControls key={turn.id}
          item={{ ...item, assignedUser: turn.speaker, timer: turn.timer, agendaEvent: speakerLimits }}
          segmentId={turn.id} liveState={liveState}
          onTimerStateChange={updateTimerState}
          meeting={meeting} getMeeting={getMeeting} isTimerManager={isTimerManager}
          isLocked={isLocked} readOnly={!isTimerManager || isLocked || turn.requiresDebaters}
          onStart={turn.id === turns[0]?.id ? () => mainTimerRef.current?.handleStart() : undefined}
          onStop={turn.id === turns[turns.length - 1]?.id ? () => mainTimerRef.current?.handleStop() : undefined}
          showClockText={showClockText} />
        <small className="d-block">{speakerName}{turn.side ? ` - ${turn.side.toUpperCase()}` : ''}</small>
        <small className="d-block">{turn.title}</small>
        {isTimerManager && !isLocked && <div className="d-flex justify-content-center gap-2 mt-1"
          onClick={event => event.stopPropagation()}>
          <Button size="sm" variant="outline-secondary" aria-label="Previous speaker" title="Previous speaker"
            disabled={index === 0 || running} onClick={() => select(index - 1)}>
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </Button>
          <Button size="sm" variant="outline-secondary" aria-label="Next speaker" title="Next speaker"
            disabled={index >= turns.length - 1 || running} onClick={() => select(index + 1)}>
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </Button>
        </div>}
        <DebateTurnTicks className="mt-2" turns={turns} index={index} onSelect={select}
          readOnly={!isTimerManager || isLocked} disabled={running} />
      </> : <small>No segments configured</small>}
    </div>}
    </div>
  </div>;
}
